// @file Make posts sortable by drag and drop on surface.
// Port of sort_mixin.js in vue composable form
import type { HTMLStyleElementWrapper } from '@@/bits/css'
import { appendCssToDocumentHead } from '@@/bits/css'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { isPostPinned } from '@@/bits/post_state'
import { observeSizeChange } from '@@/bits/resize_observer'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import type { PostDragEvent } from '@@/bits/surface_sortable'
import {
  createAndInsertCloneAtOriginalPosition,
  getIndicatorColor,
  getSortableOptions,
  isDraggedToBottomOfGroup,
  isDraggedToTopOfGroup,
  isElementPostGroup,
  NO_DRAG_SELECTOR,
  SORTABLE_GROUP_SELECTOR,
  toPostDragEvent,
  WALL_CONTAINER_ID,
  WISH_LIST_ID,
} from '@@/bits/surface_sortable'
import { useSurfaceFilterStore } from '@@/pinia/surface_filter'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostSearchStore } from '@@/pinia/surface_post_search'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { differenceBy } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { SnackbarNotificationType, useGlobalSnackbarStore } from 'pinia/global_snackbar'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfacePostsStore } from 'pinia/surface_posts'
import { useSurfaceWishArrangementStore } from 'pinia/surface_wish_arrangement'
import Sortable from 'sortablejs'
import type { WallViz } from 'types'
import { computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useForceChangeCursor } from 'vuecomposables/force_change_cursor'
import useLogger from 'vuecomposables/logger'

const SORTABLE_GROUP_ID_PREFIX = 'group-posts-'
const POST_WRAPPER_SELECTOR = '.wish-wrapper'
const POST_SELECTOR = '.wish-v2'
const CLIP_HEIGHT = 80

export function useDragSortableInGroups({
  onDragStart,
  onDragEnd,
  format = 'stream',
}: {
  /**
   * Callback when drag starts.
   */
  onDragStart?: (e: PostDragEvent) => void
  /**
   * Callback when drag ends.
   */
  onDragEnd?: (e: PostDragEvent) => void
  format: WallViz
}): void {
  const logger = useLogger('surfacePostsDragSortableInGroups')
  const { forceChangeCursor, resetCursor } = useForceChangeCursor()
  const globalSnackbarStore = useGlobalSnackbarStore()

  const surfaceSectionsStore = useSurfaceSectionsStore()
  const { backgroundLuminance, isPostSizeWide } = storeToRefs(useSurfaceStore())
  const { areWishesManuallySorted, isSectionedAndNonmanualSorted } = storeToRefs(useSurfaceWishArrangementStore())
  const { isAPostDragging, hoveredGroupIdWhenDraggingPost, postCidBeingDragged, postEntitiesById } = storeToRefs(
    useSurfacePostsStore(),
  )
  const { searchTerm } = storeToRefs(useSurfacePostSearchStore())
  const { hasSelectedFilters } = storeToRefs(useSurfaceFilterStore())

  const indicatorColor = computed(() => getIndicatorColor(backgroundLuminance.value === 'dark'))
  const canShowSectionedNonmanualSortedDragOverlay = computed(
    () =>
      isSectionedAndNonmanualSorted.value &&
      (format === 'stream' || format === 'grid' || format === 'matrix' || format === 'timeline'),
  )
  let draggedItemPlaceholder: null | Comment = null

  /**
   * STYLES WHEN DRAGGING
   */

  let sortableDefaultsStyleTag: HTMLStyleElementWrapper | null = null
  let indicatorLinePositionStyleTag: HTMLStyleElementWrapper | null = null
  let formatSortableStyleTag: HTMLStyleElementWrapper | null = null
  let listItemsClippingStyleTag: HTMLStyleElementWrapper | null = null
  let dragSizeStyleTag: HTMLStyleElementWrapper | null = null

  /**
   * DEFAULT STYLES
   */

  /**
   * (onMounted) Add default styles for sortable.
   */
  function addDefaultStyles(): void {
    sortableDefaultsStyleTag = appendCssToDocumentHead(
      `
        ${POST_WRAPPER_SELECTOR}.sortable-persistent-clone {
          opacity: 0.5;
        }
        ${POST_WRAPPER_SELECTOR}.sortable-dragging-clone {
          opacity: 1 !important;
          transform: rotate(-2deg);
          box-shadow: none !important;
          filter: drop-shadow(0px 16px 32px rgba(0, 0, 0, 0.32));
        }
      `,
    )
  }

  /**
   * (onUnmounted) Add default styles for sortable.
   */
  function removeDefaultStyles(): void {
    sortableDefaultsStyleTag?.remove()
    sortableDefaultsStyleTag = null
  }

  /**
   * STYLES WHEN DRAGGING
   **/

  let persistentCloneWrapperElement: HTMLElement | null = null
  let persistentClonePostElement: HTMLElement | null = null
  /**
   * (onStart) Create perisistent clone.
   * @param e SortableEvent
   */
  function createPersistentClone(e: Sortable.SortableEvent): void {
    const { cloneWrapperElement, cloneTargetElement } = createAndInsertCloneAtOriginalPosition(e, POST_SELECTOR)
    persistentCloneWrapperElement = cloneWrapperElement
    persistentClonePostElement = cloneTargetElement
    const resizeObserver = observeSizeChange(persistentCloneWrapperElement, () => {
      if (persistentCloneWrapperElement != null) {
        persistentCloneWrapperElement.scrollTop = clipDistanceToTopOfPost
      }
      resizeObserver?.disconnect()
    })
  }

  /**
   * (onEnd) Remove persistent clone
   */
  function removePersistentCloneWrapperElement(): void {
    if (persistentCloneWrapperElement == null) return
    persistentCloneWrapperElement.remove()
    persistentCloneWrapperElement = null
  }

  function setHiddenDroppedElementDraggingStyles(): void {
    let styleString = ''
    styleString += `
     ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
        display: none;
      }
    `

    // remove vertical/horizontal shift when hovering new post over start of new group
    // TODO: use gap instead of space on surface posts components and remove below code when gap is supported on ios 14. check again in May 2024
    if (format === 'grid' || format === 'stream') {
      styleString += `
        ${POST_WRAPPER_SELECTOR}.sortable-indicator-line:first-child + ${POST_WRAPPER_SELECTOR}:not(.sortable-indicator-line) {
        margin-top: 0 !important;
      }
    `
    } else if (format === 'timeline') {
      styleString += `
        ${POST_WRAPPER_SELECTOR}.sortable-indicator-line:first-child + ${POST_WRAPPER_SELECTOR}:not(.sortable-indicator-line) {
        margin-inline-start: 0 !important;
      }
    `
    }
    formatSortableStyleTag = appendCssToDocumentHead(styleString)
  }

  function addHorizontalIndicatorDraggingStyles({
    startOffset,
    endOffset,
    wishGapMobile,
    wishGapDesktop,
  }: {
    startOffset: number
    endOffset: number
    wishGapMobile: number
    wishGapDesktop: number
  }): void {
    const indicatorWidth = 2
    const verticalMarginMobile = wishGapMobile / 2
    const verticalMarginDesktop = wishGapDesktop / 2
    let styleString = ''
    if (format === 'grid') {
      const gridPostMaxWidth = isPostSizeWide.value ? 360 : 256
      styleString += `
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
        max-width: ${gridPostMaxWidth}px;
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line:first-child {
        inset-inline-start: 0;
        inset-inline-end: 14px;
      }
      @media (min-width: 768px) {
        ${POST_WRAPPER_SELECTOR}.sortable-indicator-line:first-child {
          inset-inline-start: 0;
          inset-inline-end: 14px;
        }
      }
      `
    } else {
      styleString += `
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
        max-width: ${persistentClonePostElement?.getBoundingClientRect().width ?? 0}px;
      }
      `
    }
    styleString += `
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line:first-child + ${POST_WRAPPER_SELECTOR}:not(.sortable-indicator-line) {
        margin-top: 0 !important;
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
        visibility: hidden;
        height: 2px;
        position: relative;
        overflow: visible !important;
        margin-top: ${verticalMarginMobile - indicatorWidth / 2}px !important;
        margin-bottom: ${-verticalMarginMobile - indicatorWidth / 2}px !important;
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line:first-child {
        position: absolute;
        margin-top: ${-verticalMarginMobile - indicatorWidth / 2}px !important;
        margin-bottom: ${verticalMarginMobile - indicatorWidth / 2}px !important;
      }
      @media (min-width: 768px) {
        ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
          margin-top: ${verticalMarginDesktop - indicatorWidth / 2}px !important;
          margin-bottom: ${-verticalMarginDesktop - indicatorWidth / 2}px !important;
        }
        ${POST_WRAPPER_SELECTOR}.sortable-indicator-line:first-child {
          position: absolute;
          margin-top: ${-verticalMarginDesktop - indicatorWidth / 2}px !important;
          margin-bottom: ${verticalMarginDesktop - indicatorWidth / 2}px !important;
        }
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line::before {
        content: '';
        visibility: visible;
        border-bottom: 2px solid ${indicatorColor.value};
        height: 0;
        position: absolute;
        top: 0;
        inset-inline-start: ${-startOffset}px;
        inset-inline-end: ${-endOffset}px;
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line > div::before {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        top: -2px;
        inset-inline-start: ${-startOffset}px;
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line > div::after {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        top: -2px;
        inset-inline-end: ${-endOffset}px;
      }
      `
    formatSortableStyleTag = appendCssToDocumentHead(styleString)
  }

  function addVerticalIndicatorDraggingStyles({ wishGapMobile, wishGapDesktop }): void {
    const indicatorWidth = 1
    const marginInlineStartMobile =
      format === 'matrix' ? -wishGapMobile / 2 - indicatorWidth : wishGapMobile / 2 - indicatorWidth
    const marginInlineEndMobile = -wishGapMobile / 2 + indicatorWidth
    const marginInlineStartDesktop =
      format === 'matrix' ? -wishGapDesktop / 2 - indicatorWidth : wishGapDesktop / 2 - indicatorWidth
    const marginInlineEndDesktop = -wishGapDesktop / 2 + indicatorWidth
    const firstChildMarginInlineStartMobile = format === 'matrix' ? marginInlineStartMobile : -wishGapMobile / 2
    const firstChildMarginInlineEndMobile = format === 'matrix' ? marginInlineEndMobile : -wishGapMobile / 2
    const firstChildMarginInlineStartDesktop = format === 'matrix' ? marginInlineStartDesktop : -wishGapDesktop / 2
    const firstChildMarginInlineEndDesktop = format === 'matrix' ? marginInlineEndDesktop : -wishGapDesktop / 2
    if (format === 'grid') {
      indicatorLinePositionStyleTag = appendCssToDocumentHead(`
        ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
          width: 0px;
        }
        ${POST_WRAPPER_SELECTOR}.sortable-indicator-line + ${POST_WRAPPER_SELECTOR} {
          margin-inline-start: 0px;
        }
      `)
    }
    formatSortableStyleTag = appendCssToDocumentHead(`
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
        visibility: hidden;
        width: 0px;
        height: ${persistentClonePostElement?.getBoundingClientRect().height ?? 0}px;
        margin-inline-start: ${marginInlineStartMobile}px !important;
        margin-inline-end: ${marginInlineEndMobile}px !important;
        position: relative;
        overflow-x: visible !important;
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line > div > div {
        display: none;
      }
       ${POST_WRAPPER_SELECTOR}.sortable-indicator-line:first-child {
        margin-inline-start: ${firstChildMarginInlineStartMobile}px !important;
        margin-inline-end: ${firstChildMarginInlineEndMobile}px !important;
      }
      @media (min-width: 768px) {
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
          margin-inline-start: ${marginInlineStartDesktop}px !important;
          margin-inline-end: ${marginInlineEndDesktop}px !important;
        }
        ${POST_WRAPPER_SELECTOR}.sortable-indicator-line:first-child {
          margin-inline-start: ${firstChildMarginInlineStartDesktop}px !important;
          margin-inline-end: ${firstChildMarginInlineEndDesktop}px !important;
        }
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line::before {
        content: '';
        visibility: visible;
        border-left: 2px solid ${indicatorColor.value};
        width: 0;
        position: absolute;
        top: 0;
        top: -6px;
        bottom: -6px;
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line > div::before {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        left: -2px;
        top: -6px;
      }
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line > div::after {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        left: -2px;
        bottom: -6px;
      }
      `)
  }
  /**
   * (onStart) Add styles for dragging on stream format.
   */
  function addStreamDraggingStyles(): void {
    addHorizontalIndicatorDraggingStyles({ startOffset: 6, endOffset: 6, wishGapMobile: 8, wishGapDesktop: 16 })
  }

  /**
   * (onStart) Add styles for dragging on matrix format.
   */
  function addMatrixDraggingStyles(): void {
    addVerticalIndicatorDraggingStyles({ wishGapMobile: 8, wishGapDesktop: 8 })
  }

  /**
   * (onStart) Add styles for dragging on timeline format.
   */
  function addTimelineDraggingStyles(): void {
    addVerticalIndicatorDraggingStyles({ wishGapMobile: 8, wishGapDesktop: 16 })
  }

  /**
   * (onStart) Add styles for dragging on grid format.
   */
  function addGridDraggingStyles(): void {
    addHorizontalIndicatorDraggingStyles({ startOffset: -4, endOffset: -14, wishGapMobile: 8, wishGapDesktop: 8 })
  }

  /**
   * (onStart) Add styles for cursor, cloned post, posts and indicator line on drag
   */
  function setDraggingStyles(): void {
    format === 'stream' && addStreamDraggingStyles()
    format === 'matrix' && addMatrixDraggingStyles()
    format === 'timeline' && addTimelineDraggingStyles()
    format === 'grid' && addGridDraggingStyles()
  }

  /**
   * (onEnd) Remove styles for cursor, cloned post, posts and indicator line after drag
   */
  function removeDraggingStyles(): void {
    dragSizeStyleTag?.remove()
    dragSizeStyleTag = null
    indicatorLinePositionStyleTag?.remove()
    indicatorLinePositionStyleTag = null
    formatSortableStyleTag?.remove()
    formatSortableStyleTag = null
  }

  /**
   * (onEnd) check if dropped post should be reverted
   * @returns boolean depending on if dropped post is reverted
   */
  function handleRevertDroppedPost(e: Sortable.SortableEvent): boolean {
    const isMovingInSameGroup = e.from === e.to
    const isPostSearchActive = searchTerm.value !== '' || hasSelectedFilters.value
    const isDraggingInSameGroup =
      !areWishesManuallySorted.value && isMovingInSameGroup && draggedItemPlaceholder != null
    if (isDraggingInSameGroup || isPostSearchActive || !canIDragPosts.value) {
      draggedItemPlaceholder?.replaceWith(e.item)
      isAPostDragging.value = false
    }

    draggedItemPlaceholder?.remove()
    draggedItemPlaceholder = null
    hoveredGroupIdWhenDraggingPost.value = undefined
    // TODO: Remove with https://linear.app/padlet/issue/PRO-7263/use-sortablejs-disable-to-prevent-dnd-and-remove-error-messages
    if (!canIDragPosts.value) {
      const message = canIModerate.value
        ? isPostSearchActive
          ? __('Cannot reorder filtered posts.')
          : __('Cannot reorder sorted posts.')
        : __('Cannot reorder posts.')

      void globalSnackbarStore.setSnackbar({
        message,
        notificationType: SnackbarNotificationType.error,
      })
    }

    return isDraggingInSameGroup || !canIDragPosts.value
  }

  let distanceFromCursorToTopOfPost = 0
  let clipDistanceToTopOfPost = 0
  /**
   * (onStart) Clip items to give more space for dragging.
   * This also remember distance from cursor to top of post for later use.
   * @param e SortableEvent
   * @see recoverScrollPositionAfterUnclip
   */
  function clipItems(e: Sortable.SortableEvent): void {
    // Do not clip items if the wall is not scrollable.
    const scrollingContainer = format === 'grid' ? e.from : document.getElementById(WALL_CONTAINER_ID)
    const fromSectionId = e.from.dataset.id
    if (format !== 'grid' && scrollingContainer?.scrollHeight === scrollingContainer?.offsetHeight) return
    const postItem = e.item.querySelector(POST_SELECTOR) as HTMLElement
    const itemRect = postItem.getBoundingClientRect()
    const itemHeight = itemRect.height ?? 0
    const originalEvent: PointerEvent = (e as any).originalEvent as PointerEvent
    let clipTop = 0
    let clipBottom = itemHeight
    if (itemHeight > CLIP_HEIGHT) {
      distanceFromCursorToTopOfPost =
        (originalEvent.pageY ?? 0) -
        ((originalEvent?.target as HTMLElement).closest('.surface-post')?.getBoundingClientRect().top ?? 0)
      clipTop = Math.max(0, distanceFromCursorToTopOfPost - CLIP_HEIGHT / 2)
      clipBottom = Math.min(itemHeight, clipTop + CLIP_HEIGHT)
      clipTop = clipBottom - CLIP_HEIGHT
      clipDistanceToTopOfPost = clipTop
    } else {
      distanceFromCursorToTopOfPost = 0
      clipDistanceToTopOfPost = 0
    }
    // On shelf, we only clip the column that the post is in.
    listItemsClippingStyleTag = appendCssToDocumentHead(`
      ${
        format === 'grid' ? `#section-${fromSectionId ?? 0}` : ''
      } ${POST_WRAPPER_SELECTOR}:not(.sortable-dragging-clone) > ${POST_SELECTOR}{
        overflow: hidden;
        max-height: ${CLIP_HEIGHT}px;
      }
      .sortable-dragging-clone > *:first-child {
        clip-path: inset(${clipTop}px 0px ${itemHeight - clipBottom}px 0px round 12px);
      }
    `)
  }

  /**
   * (onEnd) Remove clip styles from items.
   */
  function unclipItems(): void {
    listItemsClippingStyleTag?.remove()
    listItemsClippingStyleTag = null
  }

  /**
   * Calculate scrollTop for the scrolling container so that the post would be a specific distance from the top of the screen.
   * @param postOffsetTopFromScrollingContainer The offset top of the post relative to the scrolling element that we want to move to.
   * @param postScreenTop The distance to top of screen of the post that we want to move to.
   */
  function calculateScrollingContainerScrollTop(
    postOffsetTopFromScrollingContainer: number,
    postScreenTop: number,
  ): number {
    return postOffsetTopFromScrollingContainer - postScreenTop
  }

  let onStartDistanceFromPostToTop = 0
  let onStartOriginalScrollTop = 0
  let fromScrollContainer: HTMLElement | null = null
  /**
   * Remember the distance from the post to the top of the screen.
   * This is used to calculate the scrollTop of the wall container when the post is moved.
   * @param e Sorable event.
   */
  function rememberScrollPositionBeforeClip(e: Sortable.SortableEvent): void {
    fromScrollContainer = format === 'grid' ? e.from : document.getElementById(WALL_CONTAINER_ID)
    onStartDistanceFromPostToTop = e.item.getBoundingClientRect().top - (fromScrollContainer?.offsetTop ?? 0)
    onStartOriginalScrollTop = fromScrollContainer?.scrollTop ?? 0
  }

  /**
   * Recover scroll position of the wall container after the post is initially dragged.
   * Because we are "clipping" the posts, the scroll position of the wall container will be changed.
   * We need to recover the scroll position of the wall container so that the cursor is still at the same position relative to the post
   * @param e Sortable event.
   */
  function recoverScrollPositionAfterClip(e: Sortable.SortableEvent): void {
    if (fromScrollContainer != null && persistentCloneWrapperElement != null) {
      const scrollTop = calculateScrollingContainerScrollTop(
        persistentCloneWrapperElement.offsetTop - fromScrollContainer.offsetTop,
        onStartDistanceFromPostToTop,
      )
      fromScrollContainer.scrollTop = scrollTop - clipDistanceToTopOfPost
    }
  }

  let onEndDistanceFromIndicatorLineToTop = 0
  let onEndDistanceFromIndicatorLineToCursor = 0
  let toScrollContainer: HTMLElement | null = null

  /**
   * Remember the distance from the indicator line to the top of the screen and the distance from the indicator line to the cursor.
   * These values will be used to calculate the scrollTop of the wall container afterwards.
   * @param e Sortable event
   */
  function rememberScrollPositionBeforeUnclip(e: Sortable.SortableEvent): void {
    toScrollContainer = format === 'grid' ? e.to : document.getElementById(WALL_CONTAINER_ID)
    onEndDistanceFromIndicatorLineToTop = e.item.getBoundingClientRect().top - (toScrollContainer?.offsetTop ?? 0)
    const originalEvent: PointerEvent = (e as any).originalEvent as PointerEvent
    onEndDistanceFromIndicatorLineToCursor = originalEvent.pageY - e.item.getBoundingClientRect().top
  }

  /**
   * Recover scroll position to match the position of cursor before starting dragging.
   * The scroll position is adjusted so that when user drop the post, the cursor would be where
   * the user clicked on the post and started dragging.
   * @param e Sortable event.
   */
  function recoverScrollPositionAfterUnclip(
    e: Sortable.SortableEvent,
    options = { revertToOriginalDragPosition: false },
  ): void {
    if (fromScrollContainer != null && fromScrollContainer !== toScrollContainer) {
      fromScrollContainer.scrollTop = onStartOriginalScrollTop
    }
    const indicatorLineScrollTop = calculateScrollingContainerScrollTop(
      e.item.offsetTop,
      onEndDistanceFromIndicatorLineToTop,
    )
    if (toScrollContainer != null) {
      // scroll to original drag position because DnD is not allowed
      if (options.revertToOriginalDragPosition) {
        toScrollContainer.scrollTop = onStartOriginalScrollTop
      } else {
        // scroll to drop position because DnD is allowed
        const scrollTopForCursorToMatchBeforeDrag =
          indicatorLineScrollTop -
          toScrollContainer.offsetTop +
          distanceFromCursorToTopOfPost -
          onEndDistanceFromIndicatorLineToCursor
        toScrollContainer.scrollTop = scrollTopForCursorToMatchBeforeDrag
      }
    }
    onEndDistanceFromIndicatorLineToTop = 0
  }

  /**
   * (onMove)
   * @param e Sortable MoveEvent.
   */
  function checkIndicatorLinePosition(e: Sortable.MoveEvent): void {
    if (e.willInsertAfter === false && e.relatedRect.left === e.to.getBoundingClientRect().left) {
      // indicator line is on the left edge
      indicatorLinePositionStyleTag?.remove()
      indicatorLinePositionStyleTag = appendCssToDocumentHead(`
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
        width: 1px;
      }
       ${POST_WRAPPER_SELECTOR}.sortable-indicator-line + ${POST_WRAPPER_SELECTOR} {
        margin-inline-start: -1px;
      }`)
    }
    if (e.willInsertAfter === true && e.relatedRect.right === e.to.getBoundingClientRect().right) {
      // indicator line is on the right edge
      indicatorLinePositionStyleTag?.remove()
      indicatorLinePositionStyleTag = appendCssToDocumentHead(`
      ${POST_WRAPPER_SELECTOR}.sortable-indicator-line {
        width: 0px;
      }
       ${POST_WRAPPER_SELECTOR}.sortable-indicator-line + ${POST_WRAPPER_SELECTOR} {
        margin-inline-start: 0px;
      }`)
    }
  }

  /**
   * (onMove) Check if the post is moved to the bottom of the shelf when dragging over the scrollbar area in Safari.
   * If it is moved, cancel the drag.
   * @param e Sortable MoveEvent.
   * @returns false to cancel the drag.
   */
  function cancelDragOnSafariScrollbarDragover(e: Sortable.MoveEvent): boolean {
    if (!device.safari) return true
    if (e.target == null) return true
    const targetContainer = e.target as Element
    if (e.relatedRect.top > targetContainer.scrollTop + targetContainer.scrollHeight) {
      return false
    }
    return true
  }

  /**
   * Determines whether a pinned post can be dragged to the target location,
   * which may be in an "un-pinned" section.
   * And conversely, if a non-pinned post can be dragged to the target location
   * which may be in a "pinned" section
   */
  function isValidPinnedPostDrag(e: Sortable.MoveEvent): boolean {
    // if we aren't sorting manually, always allow D&D operations
    if (isSectionedAndNonmanualSorted.value) {
      return true
    }

    const draggedPostElement = e.dragged
    const relatedElement = e.related // this can either be a post or a group (if the group doesn't have posts)

    // related element is a group -> we are dragging into an empty group. we can drag there.
    if (isElementPostGroup(relatedElement)) {
      return true
    }

    // related element is a post -> we can drag there conditionally
    const draggedPostId = draggedPostElement.dataset.id
    const relatedPostId = relatedElement.dataset.id

    const draggedPost = draggedPostId == null ? undefined : postEntitiesById.value[draggedPostId]
    const relatedPost = relatedPostId == null ? undefined : postEntitiesById.value[relatedPostId]

    if (draggedPost == null || relatedPost == null) {
      return false
    }

    const targetGroup = surfaceSectionsStore.sectionEntities[relatedPost?.wall_section_id ?? -1]
    if (targetGroup?.id == null) {
      return false
    }

    if (
      // if dragged is pinned and incoming group only has unpinned posts and we are dragging to the top of the group, D&D is OK
      (isPostPinned(draggedPost) && !surfaceSectionsStore.hasPinnedPosts(targetGroup.id) && isDraggedToTopOfGroup(e)) ||
      // conversely, if post is unpinned and incoming group is entirely pinned, and we are dragging to the bottom, D&D is OK
      (!isPostPinned(draggedPost) &&
        !surfaceSectionsStore.hasUnpinnedPosts(targetGroup.id) &&
        isDraggedToBottomOfGroup(e))
    ) {
      return true
    }

    // otherwise, pinned posts can only be dragged to pinned posts. and same for unpinned posts
    if (
      (isPostPinned(draggedPost) && !isPostPinned(relatedPost)) ||
      (!isPostPinned(draggedPost) && isPostPinned(relatedPost))
    ) {
      return false
    }
    return true
  }

  /**
   * SET UP SORTABLE
   */

  const sortables = new Map<Group['id'], Sortable>()
  let initialized = false

  function initializeSortable(groupContainer: HTMLElement): void {
    // https://github.com/SortableJS/Sortable#options
    const sortable = new Sortable(groupContainer, {
      ...getSortableOptions('post', format),
      // disabled: !canIDragPosts.value TODO: remove comment in future https://linear.app/padlet/issue/PRO-7263/use-sortablejs-disable-to-prevent-dnd-and-remove-error-messages
      group: 'sortable-group', // set this to be able to drag posts between groups
      filter: NO_DRAG_SELECTOR,
      preventOnFilter: false,
      ghostClass: 'sortable-indicator-line',
      dragClass: 'sortable-dragging-clone',
      handle: POST_SELECTOR,
      fallbackOnBody: format === 'grid',
      onStart: (e) => {
        const postDragEventData = toPostDragEvent(e)
        postCidBeingDragged.value = postDragEventData.postCid
        onDragStart?.(postDragEventData)
        rememberScrollPositionBeforeClip(e)
        clipItems(e)
        createPersistentClone(e)
        forceChangeCursor('grabbing')
        // we show drag overlay instead of indicator when dragging posts when posts are nonmanual sorted and sectioned
        !canShowSectionedNonmanualSortedDragOverlay.value && setDraggingStyles()
        canShowSectionedNonmanualSortedDragOverlay.value && setHiddenDroppedElementDraggingStyles()
        recoverScrollPositionAfterClip(e)
      },
      onMove: (e): boolean => {
        if (canShowSectionedNonmanualSortedDragOverlay.value) {
          const toGroupIdString = e.to.dataset.id ?? ''
          const toGroupId = parseInt(toGroupIdString)
          hoveredGroupIdWhenDraggingPost.value = toGroupId
        }

        if (isAppUsing('pinPosts') && !isValidPinnedPostDrag(e)) {
          return false
        }

        format === 'matrix' && checkIndicatorLinePosition(e)
        if (format === 'grid') return cancelDragOnSafariScrollbarDragover(e)
        return true
      },
      onChoose(e) {
        // based on solution from https://github.com/SortableJS/Sortable/issues/837#issuecomment-1014927458
        // use a comment node as a placeholder and then in onEnd insert the old item into the placeholder when reverting
        // and remove the new item
        // TODO: remove second condition for https://linear.app/padlet/issue/PRO-7263/use-sortablejs-disable-to-prevent-dnd-and-remove-error-messages
        if (!areWishesManuallySorted.value || !canIDragPosts.value) {
          draggedItemPlaceholder = document.createComment('dragged-item-placeholder')
          e.item.after(draggedItemPlaceholder)
        }
      },
      onEnd: (e) => {
        removePersistentCloneWrapperElement()
        rememberScrollPositionBeforeUnclip(e)
        removeDraggingStyles()
        resetCursor()
        unclipItems()

        const droppedPostReverted = handleRevertDroppedPost(e)
        if (droppedPostReverted) {
          recoverScrollPositionAfterUnclip(e, { revertToOriginalDragPosition: true })
          postCidBeingDragged.value = undefined
          return
        }
        recoverScrollPositionAfterUnclip(e, { revertToOriginalDragPosition: false })

        onDragEnd?.(toPostDragEvent(e))
        postCidBeingDragged.value = undefined
      },
    })
    sortables.set(groupContainer.id, sortable)
  }

  function initializeAllSortables(): void {
    if (initialized) return
    const groupContainers = document
      .getElementById(WISH_LIST_ID)
      ?.querySelectorAll<HTMLElement>(SORTABLE_GROUP_SELECTOR)
    groupContainers?.forEach((groupContainer) => {
      if (!sortables.has(groupContainer.id)) {
        initializeSortable(groupContainer)
      }
    })
    logger.log('number of groups initialized', groupContainers?.length)
    initialized = true
  }

  function destroyAllSortables(): void {
    sortables.forEach((s) => s.destroy())
    sortables.clear()
    initialized = false
  }

  function addSortableGroups(...groups: Group[]): void {
    logger.log(groups.map((g) => g.id))
    const groupContainers = groups
      .map((group) => document.getElementById(`${SORTABLE_GROUP_ID_PREFIX}${group.id}`))
      .filter((el) => el != null) as HTMLElement[]
    groupContainers.forEach((groupContainer) => {
      if (!sortables.has(groupContainer.id)) {
        initializeSortable(groupContainer)
      }
    })
  }

  function removeSortableGroups(...groups: Group[]): void {
    groups.forEach((group) => {
      const groupContainerId = `${SORTABLE_GROUP_ID_PREFIX}${group.id}`
      sortables.get(groupContainerId)?.destroy()
      sortables.delete(groupContainerId)
    })
  }

  /**
   * ACTIVATE SORTABLE
   */

  const { isOverlayVisible } = storeToRefs(useSurfaceStore())
  const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
  const { canIDragPosts } = storeToRefs(useSurfacePostsStore())
  const { sortedGroups } = storeToRefs(useSurfaceWishArrangementStore())

  // When groups are added or removed, we need to add (to enable dragging)
  // or remove (to clean up memory) the sortable instances.
  watch(sortedGroups, (newGroups, oldGroups) => {
    logger.log('sortedGroups', sortedGroups)
    logger.log('sortedGroups - initialized', initialized)
    if (!initialized) return
    nextTick(() => {
      if (oldGroups != null) {
        addSortableGroups(...differenceBy(newGroups, oldGroups, 'id'))
        removeSortableGroups(...differenceBy(oldGroups, newGroups, 'id'))
      } else {
        addSortableGroups(...newGroups)
      }
    })
  })

  // TODO: add canIDragPosts as watch dependency when we remove error message https://linear.app/padlet/issue/PRO-7263/use-sortablejs-disable-to-prevent-dnd-and-remove-error-messages
  watch([isOverlayVisible], ([isVisible]) => {
    if (isVisible) {
      sortables.forEach((sortable) => sortable.option('disabled', true))
    } else {
      sortables.forEach((sortable) => sortable.option('disabled', false))
    }
  })

  onMounted(() => {
    nextTick(() => {
      addDefaultStyles()
      initializeAllSortables()
    })
  })

  onUnmounted(() => {
    removeDefaultStyles()
    destroyAllSortables()
  })
}
